import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash/fp';
import { useCallback } from 'react';
import styled from 'styled-components';

import useIsVTEInternal from 'dashboard/apps/VerificationTemplateEditor/hooks/useIsVTEInternal';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { ProductFeatureName } from 'dashboard/models/organization';
import { AvailableOrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import {
  VerificationTemplate,
  VerificationTemplateBuilders,
  VerificationTemplateFilterOption,
  VerificationTemplateStatus,
} from 'dashboard/models/verification-template';
import { VerificationTemplatesFilterParams } from 'dashboard/redux/actions/verification-templates';
import { initialVerificationTemplatesFilterState } from 'dashboard/redux/reducers/verification-templates';
import CreateVerificationTemplateModal from 'dashboard/views/DashboardVerificationTemplatesView/CreateVerificationTemplateModal';
import VerificationTemplatesTable from 'dashboard/views/DashboardVerificationTemplatesView/VerificationTemplatesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';
import { Permission } from 'lib/permissions';

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: VerificationTemplateStatus.Active,
  },
  {
    label: 'Inactive',
    value: VerificationTemplateStatus.Inactive,
  },
];

export const TYPE_FILTER_OPTIONS: Array<{
  label: string;
  value: VerificationTemplate['type'];
}> = Object.entries(VERIFICATION_TEMPLATE_TYPE_NAMES_SHORT)
  .map(([verificationType, label]) => ({
    label,
    value: verificationType as VerificationTemplate['type'],
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

const modalStyles: ReactModal.Styles = {
  content: {
    overflowX: 'hidden',
  },
};

interface DashboardVerificationTemplatesViewProps {
  featureFlags: FeatureFlagsMap;
  isLoading: boolean;
  verificationTemplates: VerificationTemplate[];
  verificationTemplateBasics: VerificationTemplateFilterOption[];
  verificationTemplateBuilders: VerificationTemplateBuilders;
  loadingAvailablePlans: boolean;
  paginationParams: PaginationParams;
  sortParams: SortParams;
  filters: VerificationTemplatesFilterParams;
  pageSize: number;
  handleOmniSearch: (omniQuery: string) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStatusFilterUpdate: (status: VerificationTemplateStatus[]) => void;
  handleTypeFilterUpdate: (type: VerificationTemplate['type'][]) => void;
  handleCreateVerificationTemplate: (builderName: string) => Promise<void>;
  handleLoadAvailablePlans: (
    requiredProductFeatures: ProductFeatureName[],
  ) => Promise<AvailableOrganizationPlan[]>;
  handleCreateOrganizationPlanMigration: (
    inquiryId: string,
    selectedPlanKey: string,
  ) => Promise<OrganizationPlanMigration>;
  showMessage: (message: string) => void;
  updatePayment: (stripeId: string) => Promise<void>;
}

const DashboardVerificationTemplatesView = ({
  isLoading,
  verificationTemplates,
  verificationTemplateBasics,
  verificationTemplateBuilders,
  loadingAvailablePlans,
  paginationParams,
  sortParams,
  filters,
  pageSize,
  handleOmniSearch,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
  handleStatusFilterUpdate,
  handleTypeFilterUpdate,
  handleCreateVerificationTemplate,
  handleLoadAvailablePlans,
  handleCreateOrganizationPlanMigration,
  showMessage,
  updatePayment,
}: DashboardVerificationTemplatesViewProps) => {
  const hasTemplateCreatePermission = usePermission(Permission.VerificationTemplateCreate, {
    global: true,
  });
  const createVerificationTemplateEnabled = useFeatureFlag(
    'dashboard:create-verification-template',
  );
  const [
    showCreateVerificationTemplatesModal,
    { enable: openCreateVerificationTemplatesModal, disable: hideCreateVerificationTemplatesModal },
  ] = useToggle();

  const handleCloseModal = useCallback(() => {
    if (!isLoading) {
      hideCreateVerificationTemplatesModal();
    }
  }, [isLoading, hideCreateVerificationTemplatesModal]);
  const isVTEInternal = useIsVTEInternal();
  const canCreateTemplates =
    createVerificationTemplateEnabled && (isVTEInternal || hasTemplateCreatePermission);

  const { status, type, omni } = filters;

  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'Statuses', {
    emptyIsAll: true,
  });

  const typeFilterLabelTextMapper = useLabelTextMapper(TYPE_FILTER_OPTIONS, 'Types', {
    emptyIsAll: true,
  });

  // A filter is set by default so we check for equality rather than emptiness
  const hasUsedFilters = !isEqual(filters, initialVerificationTemplatesFilterState);

  const filterConfig: FilterConfig[] = [
    {
      id: 'status',
      name: 'Status',
      type: 'multiselect-legacy',
      selectOptionValues: STATUS_FILTER_OPTIONS,
      labelTextMapper: statusFilterLabelTextMapper,
      onChange: (value) => {
        handleStatusFilterUpdate(value as VerificationTemplateStatus[]);
      },
      value: status || [],
    },
    {
      id: 'type',
      name: 'Type',
      type: 'multiselect-legacy',
      selectOptionValues: TYPE_FILTER_OPTIONS,
      labelTextMapper: typeFilterLabelTextMapper,
      onChange: (value) => {
        handleTypeFilterUpdate(value as VerificationTemplate['type'][]);
      },
      value: type || [],
    },
  ];

  return (
    <>
      <Container>
        <InactiveEnvironmentBanner />
        <Header title='Verification templates'>
          {canCreateTemplates && (
            <HStack>
              <Button
                variant='primary'
                id='inquiry-templates-view__create-verification-template-button'
                name='create-verification-template.button'
                prefixIcon={<PlusIcon />}
                onClick={openCreateVerificationTemplatesModal}
              >
                Create template
              </Button>
            </HStack>
          )}
        </Header>
        <Section data-test='verification-templates-view'>
          <Card>
            <Card.Body>
              <TableTitleContainer>
                <Card.Title>Verification template list</Card.Title>
              </TableTitleContainer>
              <TableControls
                searchProps={{
                  id: 'verifications__search',
                  handleInput: handleOmniSearch,
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  value: omni,
                  placeholder: 'Search by verification template ID or name',
                }}
                filters={filterConfig}
              />
              <VerificationTemplatesTable
                verificationTemplates={verificationTemplates}
                verificationTemplateBasics={verificationTemplateBasics}
                sortParams={sortParams}
                hasUsedFilters={hasUsedFilters}
                isLoading={isLoading}
                handleSortUpdate={handleSortUpdate}
              />
              {(verificationTemplateBasics?.length > 0 || verificationTemplates?.length > 0) && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={paginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
        <ModalWrapper
          shouldCloseOnEsc={true}
          styleOverride={modalStyles}
          isOpen={showCreateVerificationTemplatesModal}
          handleCloseModal={handleCloseModal}
        >
          <CreateVerificationTemplateModal
            verificationTemplateBuilders={verificationTemplateBuilders}
            loadingAvailablePlans={loadingAvailablePlans}
            handleCloseModal={hideCreateVerificationTemplatesModal}
            handleCreateVerificationTemplate={handleCreateVerificationTemplate}
            handleLoadAvailablePlans={handleLoadAvailablePlans}
            handleCreateOrganizationPlanMigration={handleCreateOrganizationPlanMigration}
            showMessage={showMessage}
            updatePayment={updatePayment}
          />
        </ModalWrapper>
      </Container>
    </>
  );
};

export default DashboardVerificationTemplatesView;
